import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { withStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';

import { Field, reduxForm, SubmissionError } from 'redux-form';

import Section, { SectionHeader, SectionTitle, SectionBody } from 'components/common/Section';
import Button from 'components/common/Button';
import TextField from 'components/fields/TextField';
import CheckboxField from 'components/fields/CheckboxField';
import FormError from 'components/common/FormError';

import { updateSystemSettings } from 'redux/modules/systemSettings';

const styles = theme => ({
  grid: {
    marginBottom: 10,
    [theme.breakpoints.down('sm')]: {
      marginBottom: 4,
    },
  },
  gridGutter: {
    marginTop: 15,
  },
  button: {
    '& + $button': {
      marginLeft: 16,
      [theme.breakpoints.down('sm')]: {
        marginLeft: 8,
      },
    },
  },
  form: {
    width: 368,
    margin: '10px auto',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },
});

function onSubmit(values, dispatch) {
  return new Promise((resolve, reject) => {
    dispatch(updateSystemSettings({
      values,
      resolve,
      reject,
    }));
  }).then(res => res,
    (err) => {
      let formError = [];
      Object.keys(err).forEach((key) => {
        if (typeof err[key] !== typeof values[key]) {
          formError = [
            ...formError,
            `${key}: ${err[key]}`,
          ];
        }
      });
      throw new SubmissionError({
        ...err,
        ...formError.length > 0 ? { _error: formError.join('; ') } : {},
      });
    });
}

function onSubmitFail(errors) {
  const errorNames = Object.keys(errors).reduce((accumulator, currentValue) => ([
    ...accumulator,
    ...Object.keys(errors[currentValue]).map(item => `${currentValue}.${item}`),
  ]), []);
  if (errorNames.length > 0) {
    const selectorString = errorNames.map(item => `[name="${item}"]`).join(', ');
    const firstErrorEl = document.querySelector(selectorString);
    if (firstErrorEl) {
      const firstErrorElOffsetTop = firstErrorEl.getBoundingClientRect().top;
      const windowScrollTop = window.scrollY !== undefined ? window.scrollY : window.scrollTop;
      const scrollOffset = 50;
      if ((window.innerHeight - scrollOffset) < firstErrorElOffsetTop) {
        const pageHeaderHeight = 64;
        window.scrollTo(0, (firstErrorElOffsetTop
          + windowScrollTop) - pageHeaderHeight - scrollOffset);
        firstErrorEl.focus();
      }
    }
  }
}

const SystemSettingsEditForm = ({
  submit,
  reset,
  handleSubmit,
  submitting,
  initialValues,
  error,
  classes,
}) => (
  <Section>
    <SectionHeader fixed>
      <SectionTitle>
        Edit System Settings
      </SectionTitle>
      <div>
        <Button
          className={classes.button}
          variant="raised"
          onClick={reset}
        >
          Reset
        </Button>
        <Button
          className={classes.button}
          variant="raised"
          color="primary"
          disabled={submitting}
          onClick={submit}
        >
          Save
        </Button>
      </div>
    </SectionHeader>
    <SectionBody>
      <form
        className={classes.form}
        noValidate
        autoComplete="off"
        onSubmit={handleSubmit}
      >
        {error &&
          <Grid
            className={classes.grid}
            container
            spacing={24}
          >
            <Grid
              item
              xs={12}
            >
              <FormError>
                {error}
              </FormError>
            </Grid>
          </Grid>
        }
        {Object.keys(initialValues).map(fields => (
          <Fragment key={fields}>
            <Grid
              className={classNames(classes.grid, classes.gridGutter)}
              container
              spacing={24}
            >
              <Grid item xs={12}>
                <Typography className={classes.typography} color="secondary">
                  {fields}
                </Typography>
              </Grid>
            </Grid>
            {Object.keys(initialValues[fields]).filter(field => field !== 'PROFIT_PERCENT').map(field => (
              <Grid
                key={field}
                className={classes.grid}
                container
                justify="center"
                spacing={24}
              >
                <Grid
                  item
                  xs={12}
                >
                  <Field
                    id={field}
                    name={`${fields}.${field}`}
                    label={field}
                    {...typeof initialValues[fields][field] === 'boolean' ? {
                      component: CheckboxField,
                    } : {
                      component: TextField,
                      fullWidth: true,
                      ...field.toLowerCase().indexOf('password') !== -1 ? { type: 'password' } : {},
                    }}
                  />
                </Grid>
              </Grid>
            ))}
          </Fragment>
        ))}
      </form>
    </SectionBody>
  </Section>
);

SystemSettingsEditForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  submit: PropTypes.func.isRequired,
  reset: PropTypes.func.isRequired,
  submitting: PropTypes.bool.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  initialValues: PropTypes.object.isRequired,
  error: PropTypes.string,
  classes: PropTypes.shape({
    grid: PropTypes.string.isRequired,
    gridGutter: PropTypes.string.isRequired,
    button: PropTypes.string.isRequired,
    form: PropTypes.string.isRequired,
  }).isRequired,
};

SystemSettingsEditForm.defaultProps = {
  error: null,
};

export default reduxForm({
  onSubmit,
  onSubmitFail,
  enableReinitialize: true,
})(withStyles(styles)(SystemSettingsEditForm));



// WEBPACK FOOTER //
// ./src/components/settings/SystemSettingsEditForm/SystemSettingsEditForm.js